<template>
  <div class="customer-service-page">
    <Navbar />
    <LightBlueBanner title="支援中心" />
    <Breadcrumb :items="breadcrumbItems" />

    <!-- 提問須知 (滿版) -->
    <div class="notice-section">
      <div class="notice-block">
        <h2 class="notice-title">提問須知</h2>
        <p class="notice-intro">
          歡迎使用本網站支援中心，為確保您的權益，請務必詳閱以下說明內容，再行提問：
        </p>
        <ul class="notice-list">
          <li>
            依個人資料保護法，未經您同意，衛福部資訊處不會將您的個人資料提供他人使用，僅提供承辦單位業務需要聯繫使用。
          </li>

          <li>
            依行政程序法第173條規定，有下列情形之一者，衛福部資訊處將不予處理：
            <ol class="sub-list">
              <li>無具體內容或未具真實姓名及Email者。</li>
              <li>同一事由，經予適當處理，並已明確答覆後，而仍一再陳情者。</li>
              <li>
                非主管陳情內容之機關，接獲陳情人以同一事由分向各機關陳情者。
              </li>
            </ol>
          </li>
          <li>
            提問案件處理期限，為系統收到您的確認信件後「6個工作天(扣除例假日)」為原則。
          </li>
        </ul>
      </div>
    </div>

    <div class="page-content">
      <div class="main-layout">
        <!-- 表單區 -->
        <div class="form-section">
          <form class="inquiry-form" novalidate @submit.prevent="handleSubmit">
            <div class="form-row">
              <div class="form-group">
                <label class="form-label" for="cs-name"><span class="required">*</span>您的姓名</label>
                <input
                  id="cs-name"
                  v-model="formData.name"
                  type="text"
                  class="form-input"
                  :class="{ 'is-error': fieldErrors.name }"
                  placeholder="請輸入您的姓名"
                  :disabled="submitting"
                  @input="clearFieldError('name')"
                />
                <span v-if="fieldErrors.name" class="field-error-msg">{{ fieldErrors.name }}</span>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label class="form-label" for="cs-organization"><span class="required">*</span>服務單位</label>
                <input
                  id="cs-organization"
                  v-model="formData.organization"
                  type="text"
                  class="form-input"
                  :class="{ 'is-error': fieldErrors.organization }"
                  placeholder="請輸入服務單位名稱"
                  :disabled="submitting"
                  @input="clearFieldError('organization')"
                />
                <span v-if="fieldErrors.organization" class="field-error-msg">{{ fieldErrors.organization }}</span>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label class="form-label" for="cs-phone"><span class="required">*</span>聯絡電話</label>
                <input
                  id="cs-phone"
                  v-model="formData.phone"
                  type="tel"
                  class="form-input"
                  :class="{ 'is-error': fieldErrors.phone }"
                  placeholder="請輸入聯絡電話（例：0912345678 或 02-12345678）"
                  :disabled="submitting"
                  @input="clearFieldError('phone')"
                />
                <span v-if="fieldErrors.phone" class="field-error-msg">{{ fieldErrors.phone }}</span>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label class="form-label" for="cs-email"><span class="required">*</span>電子信箱</label>
                <input
                  id="cs-email"
                  v-model="formData.email"
                  type="email"
                  class="form-input"
                  :class="{ 'is-error': fieldErrors.email }"
                  placeholder="請輸入電子信箱"
                  :disabled="submitting"
                  @input="clearFieldError('email')"
                />
                <span v-if="fieldErrors.email" class="field-error-msg">{{ fieldErrors.email }}</span>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label class="form-label" for="cs-category"><span class="required">*</span>問題分類</label>
                <select
                  id="cs-category"
                  v-model="formData.category"
                  class="form-input"
                  :class="{ 'is-error': fieldErrors.category }"
                  :disabled="submitting"
                  @change="clearFieldError('category')"
                >
                  <option value="">請選擇問題分類</option>
                  <option value="ACCOUNT_ACCESS">帳號與登入</option>
                  <option value="COURSE_CERTIFICATE">課程與證書</option>
                  <option value="WORKSHOP_EVENT">工作坊與活動</option>
                  <option value="STANDARD_CONTENT">標準內容</option>
                  <option value="SYSTEM_TECHNICAL">系統與技術問題</option>
                  <option value="PARTNERSHIP_OTHER">合作與其他</option>
                </select>
                <span v-if="fieldErrors.category" class="field-error-msg">{{ fieldErrors.category }}</span>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label class="form-label" for="cs-content"><span class="required">*</span>請寫下您想詢問的問題...</label>
                <textarea
                  id="cs-content"
                  v-model="formData.content"
                  class="form-textarea"
                  :class="{ 'is-error': fieldErrors.content }"
                  rows="5"
                  placeholder="請詳細描述您的問題或建議..."
                  :disabled="submitting"
                  @input="clearFieldError('content')"
                ></textarea>
                <span v-if="fieldErrors.content" class="field-error-msg">{{ fieldErrors.content }}</span>
              </div>
            </div>

            <p class="terms-notice">
              本人已詳細閱讀並同意<a @click.prevent="showPrivacyModal = true" class="link">隱私權政策及會員服務條款</a>，並同意提供所需之個人資料以利承辦單位業務聯繫使用。
            </p>

            <RecaptchaWidget
              ref="recaptchaRef"
              :error-message="fieldErrors.recaptcha"
            />

            <button type="submit" class="submit-btn" :disabled="submitting">
              {{ submitting ? "提交中..." : "提交問題" }}
            </button>
          </form>
        </div>

        <!-- FOLLOW US -->
        <div class="follow-us-block">
          <div class="follow-us-inner">
            <h3 class="follow-us-title">FOLLOW<br />US！</h3>
            <p class="follow-us-subtitle">隨時追蹤最新資訊</p>
            <div class="social-icons">
              <a href="#" class="social-icon" title="LINE" target="_blank">
                <img src="/customer-service/line.svg" alt="LINE" />
              </a>
              <a href="#" class="social-icon" title="Facebook" target="_blank">
                <img src="/customer-service/fb.svg" alt="Facebook" />
              </a>
              <a href="#" class="social-icon" title="Threads" target="_blank">
                <img src="/customer-service/threads.svg" alt="Threads" />
              </a>
              <a href="#" class="social-icon" title="LinkedIn" target="_blank">
                <img src="/customer-service/linkedin.svg" alt="LinkedIn" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <FooterSection />

    <PrivacyPolicyModal
      v-model:open="showPrivacyModal"
      @agree="showPrivacyModal = false"
      @cancel="showPrivacyModal = false"
    />
  </div>
</template>

<script setup>
import { reactive, ref } from "vue";
import { message } from "ant-design-vue";
import Navbar from "@/components/Navbar.vue";
import FooterSection from "@/components/FooterSection.vue";
import LightBlueBanner from "@/components/LightBlueBanner.vue";
import Breadcrumb from "@/components/Breadcrumb.vue";
import PrivacyPolicyModal from "@/components/PrivacyPolicyModal.vue";
import RecaptchaWidget from "@/components/RecaptchaWidget.vue";
import { msSupportAPI } from "@/services/api.js";

const breadcrumbItems = [{ label: "首頁", path: "/" }, { label: "支援中心" }];

const submitting = ref(false);
const showPrivacyModal = ref(false);
const recaptchaRef = ref(null);

const formData = reactive({
  name: "",
  organization: "",
  phone: "",
  email: "",
  category: "",
  content: "",
});

const fieldErrors = reactive({
  name: "",
  organization: "",
  phone: "",
  email: "",
  category: "",
  content: "",
  recaptcha: "",
});

const clearFieldError = (field) => {
  if (fieldErrors[field]) {
    fieldErrors[field] = "";
  }
};

const validateForm = () => {
  let isValid = true;
  Object.keys(fieldErrors).forEach((key) => (fieldErrors[key] = ""));

  const trimmedName = formData.name.trim();
  if (!trimmedName) {
    fieldErrors.name = "請輸入您的姓名";
    isValid = false;
  } else if (trimmedName.length > 50) {
    fieldErrors.name = "姓名長度請勿超過 50 個字元";
    isValid = false;
  }

  const trimmedOrg = formData.organization.trim();
  if (!trimmedOrg) {
    fieldErrors.organization = "請輸入服務單位";
    isValid = false;
  } else if (trimmedOrg.length > 100) {
    fieldErrors.organization = "服務單位長度請勿超過 100 個字元";
    isValid = false;
  }

  const trimmedPhone = formData.phone.trim();
  const phonePattern = /^09\d{8}$|^0\d{1,2}-?\d{6,8}$|^\+?[0-9\s\-()]{8,20}$/;
  if (!trimmedPhone) {
    fieldErrors.phone = "請輸入聯絡電話";
    isValid = false;
  } else if (!phonePattern.test(trimmedPhone)) {
    fieldErrors.phone = "請輸入有效的電話號碼（如：0912345678 或 02-12345678）";
    isValid = false;
  }

  const trimmedEmail = formData.email.trim();
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!trimmedEmail) {
    fieldErrors.email = "請輸入電子信箱";
    isValid = false;
  } else if (!emailPattern.test(trimmedEmail)) {
    fieldErrors.email = "請輸入有效的電子信箱格式";
    isValid = false;
  }

  if (!formData.category) {
    fieldErrors.category = "請選擇問題分類";
    isValid = false;
  }

  const trimmedContent = formData.content.trim();
  if (!trimmedContent) {
    fieldErrors.content = "請寫下您想詢問的問題";
    isValid = false;
  } else if (trimmedContent.length < 5) {
    fieldErrors.content = "問題內容請至少填寫 5 個字";
    isValid = false;
  } else if (trimmedContent.length > 3000) {
    fieldErrors.content = "問題內容請勿超過 3000 個字元";
    isValid = false;
  }

  return isValid;
};

const handleSubmit = async () => {
  if (!validateForm()) {
    message.warning("請填寫所有必填欄位並確認格式正確");
    return;
  }

  submitting.value = true;
  clearFieldError("recaptcha");

  try {
    let recaptchaToken = null;
    if (recaptchaRef.value) {
      try {
        recaptchaToken = await recaptchaRef.value.execute("customer_service");
      } catch (recaptchaErr) {
        console.warn("reCAPTCHA v3 execute failed:", recaptchaErr);
      }
    }

    await msSupportAPI.submitInquiry({
      name: formData.name.trim(),
      organization: formData.organization.trim(),
      phone: formData.phone.trim(),
      email: formData.email.trim(),
      category: formData.category,
      content: formData.content.trim(),
      recaptcha_token: recaptchaToken || undefined,
    });
    message.success("詢問已送出，我們將盡快為您處理！");
    Object.assign(formData, {
      name: "",
      organization: "",
      phone: "",
      email: "",
      category: "",
      content: "",
    });
    Object.keys(fieldErrors).forEach((key) => (fieldErrors[key] = ""));
  } catch (error) {
    const serverErrors = error?.response?.data?.errors || error?.response?.data?.content?.errors;
    if (error?.response?.status === 422 && serverErrors) {
      Object.keys(serverErrors).forEach((key) => {
        const errorMsg = Array.isArray(serverErrors[key])
          ? serverErrors[key][0]
          : serverErrors[key];
        if (key === 'recaptcha_token' || key === 'recaptcha') {
          fieldErrors.recaptcha = errorMsg;
        } else if (key in fieldErrors) {
          fieldErrors[key] = errorMsg;
        }
      });
      message.error(error.response?.data?.message || "資料驗證失敗，請檢查輸入內容");
    } else {
      message.error(error?.response?.data?.message || "提交失敗，請稍後再試。");
    }
  } finally {
    submitting.value = false;
  }
};
</script>

<style scoped lang="scss">
.customer-service-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.recaptcha-row {
  margin-bottom: 20px;
}


.page-content {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 48px 20px 80px;
  box-sizing: border-box;
}

/* ── Top section: notice ── */
.notice-section {
  width: 100%;
  background: #ededed;
  display: flex;
  justify-content: center;
}

.notice-block {
  max-width: 1200px;
  width: 100%;
  padding: 40px 20px;
  box-sizing: border-box;
}

.notice-title {
  font-family: "Noto Sans TC", sans-serif;
  font-weight: 500;
  font-size: 32px;
  line-height: 44px;
  letter-spacing: 0.02em;
  color: #333333;
  margin: 0 0 20px;
}

.notice-intro {
  font-family: "Noto Sans TC", sans-serif;
  font-size: 16px;
  line-height: 1.75;
  color: #333333;
  margin: 0 0 16px;
}

.notice-list {
  margin: 0;
  padding-left: 20px;
  font-family: "Noto Sans TC", sans-serif;
  font-size: 16px;
  line-height: 1.75;
  color: #333333;

  li {
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.sub-list {
  list-style-type: lower-alpha;
  margin: 8px 0 0;
  padding-left: 24px;

  li {
    margin-bottom: 4px;
  }
}

/* ── Follow Us ── */
.follow-us-block {
  width: 320px;
  flex-shrink: 0;
  background: linear-gradient(242.69deg, #fff9b9 1.76%, #def3b0 98.98%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.follow-us-inner {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 40px 32px;
  text-align: left;
  width: 100%;
}

.follow-us-title {
  font-family: "Noto Sans TC", sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: #000000;
  line-height: 1.2;
  margin: 0 0 16px;
}

.follow-us-subtitle {
  font-family: "Noto Sans TC", sans-serif;
  font-size: 14px;
  color: #333333;
  margin: 0 0 24px;
}

.social-icons {
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
  justify-content: flex-start;
}

.social-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  transition: transform 0.2s ease, opacity 0.2s ease;

  &:hover {
    transform: scale(1.1);
    opacity: 0.9;
  }

  img {
    display: block;
    width: 100%;
    height: auto;
  }
}

/* ── Main Layout ── */
.main-layout {
  display: flex;
  gap: 40px;
  align-items: flex-start;
  justify-content: space-between;
}

/* ── Form section ── */
.form-section {
  flex: 1;
  max-width: 720px;
}

.inquiry-form {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.form-row {
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-family: "Noto Sans TC", sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: #333333;

  .required {
    color: #d32f2f;
    margin-right: 2px;
  }
}

.form-input,
.form-textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #cccccc;
  border-radius: 4px;
  padding: 10px 14px;
  font-family: "Noto Sans TC", sans-serif;
  font-size: 15px;
  color: #333333;
  background: #ffffff;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: #0079bb;
  }

  &.is-error {
    border-color: #ff4d4f;
    box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.15);
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }
}

.field-error-msg {
  display: block;
  font-family: "Noto Sans TC", sans-serif;
  font-size: 13px;
  color: #ff4d4f;
  margin-top: 2px;
  line-height: 1.4;
}

.form-textarea {
  resize: vertical;
  min-height: 120px;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 28px;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-family: "Noto Sans TC", sans-serif;
  font-size: 14px;
  color: #333333;
  line-height: 1.6;
  cursor: pointer;

  input[type="checkbox"] {
    margin-top: 3px;
    flex-shrink: 0;
    accent-color: #0079bb;
    width: 15px;
    height: 15px;
    cursor: pointer;
  }
}

.link {
  color: #0079bb;
  text-decoration: underline;

  &:hover {
    color: #004a89;
  }
}

.terms-notice {
  font-family: "Noto Sans TC", sans-serif;
  font-size: 14px;
  color: #414143;
  line-height: 1.6;
  margin: 16px 0 24px;
}

.submit-btn {
  width: 100%;
  padding: 14px 0;
  background: #0079bb;
  color: #ffffff;
  border: none;
  border-radius: 50px;
  font-family: "Noto Sans TC", sans-serif;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: #006296;
  }

  &:disabled {
    background: #d9d9d9;
    cursor: not-allowed;
  }
}

/* ── RWD ── */
@media (max-width: 768px) {
  .main-layout {
    flex-direction: column;
  }

  .follow-us-block {
    width: 100%;
  }

  .follow-us-inner {
    align-items: center;
    text-align: center;
  }

  .social-icons {
    flex-direction: row;
    justify-content: center;
  }

  .notice-block {
    padding: 28px 24px;
  }

  .page-content {
    padding: 32px 16px 60px;
  }

  .form-section {
    max-width: 100%;
  }
}

@media (max-width: 480px) {
  .notice-title {
    font-size: 24px;
    line-height: 36px;
  }

  .page-content {
    padding: 24px 16px 48px;
  }
}
</style>
